import { useState, useEffect } from 'react';
import { Settings, Code } from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import CollapsibleSection from './CollapsibleSection';
import TagInput from './TagInput';
import SaveToRepoButton from './SaveToRepoButton';
import { useClaudeConfig } from './useClaudeConfig';

interface ClaudeSettings {
  permissions?: {
    allow?: string[];
    deny?: string[];
    ask?: string[];
    defaultMode?: string;
  };
  env?: Record<string, string>;
  model?: string;
  effortLevel?: string;
}

const DEFAULT_MODES = ['default', 'acceptEdits', 'plan', 'auto', 'dontAsk'];
const MODELS = ['', 'claude-opus-4-6', 'claude-sonnet-4-6', 'claude-haiku-4-5-20251001'];
const EFFORT_LEVELS = ['', 'low', 'medium', 'high', 'xhigh', 'max'];

export default function SettingsSection() {
  const config = useClaudeConfig('.claude/settings.json');
  const [settings, setSettings] = useState<ClaudeSettings>({});
  const [rawMode, setRawMode] = useState(false);
  const [envKey, setEnvKey] = useState('');
  const [envVal, setEnvVal] = useState('');

  useEffect(() => {
    if (config.content) {
      try {
        setSettings(JSON.parse(config.content));
      } catch {
        setSettings({});
      }
    }
  }, [config.content]);

  function updateSettings(patch: Partial<ClaudeSettings>) {
    const updated = { ...settings, ...patch };
    setSettings(updated);
    config.setContent(JSON.stringify(updated, null, 2));
  }

  function updatePermissions(key: 'allow' | 'deny' | 'ask', value: string[]) {
    updateSettings({
      permissions: { ...settings.permissions, [key]: value },
    });
  }

  function addEnvVar() {
    if (!envKey.trim()) return;
    updateSettings({ env: { ...settings.env, [envKey.trim()]: envVal } });
    setEnvKey('');
    setEnvVal('');
  }

  function removeEnvVar(key: string) {
    const next = { ...settings.env };
    delete next[key];
    updateSettings({ env: next });
  }

  return (
    <CollapsibleSection
      title="Settings & Permissions"
      description="Permission rules, environment variables, model defaults"
      filePath=".claude/settings.json"
      icon={Settings}
      exists={config.exists}
      loading={config.loading}
    >
      {config.loading ? (
        <LoadingState size="md" />
      ) : (
        <div className="space-y-5">
          {/* Toggle raw JSON */}
          <div className="flex justify-end">
            <button
              onClick={() => setRawMode(!rawMode)}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              <Code className="w-3.5 h-3.5" />
              {rawMode ? 'Form view' : 'Raw JSON'}
            </button>
          </div>

          {rawMode ? (
            <textarea
              value={config.content || '{}'}
              onChange={(e) => config.setContent(e.target.value)}
              className="w-full h-64 p-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-mono text-slate-900 dark:text-slate-100 resize-y"
            />
          ) : (
            <>
              {/* Permissions */}
              <fieldset className="space-y-3">
                <legend className="text-sm font-medium text-slate-700 dark:text-slate-300">Permissions</legend>

                <div>
                  <label className="block text-xs text-slate-500 mb-1">
                    Default mode
                  </label>
                  <select
                    value={settings.permissions?.defaultMode || 'default'}
                    onChange={(e) =>
                      updateSettings({
                        permissions: { ...settings.permissions, defaultMode: e.target.value },
                      })
                    }
                    className="w-48 px-2 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100"
                  >
                    {DEFAULT_MODES.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-slate-500 mb-1">
                    Allow rules <span className="text-slate-400">e.g. Bash(npm *), Read(~/.zshrc)</span>
                  </label>
                  <TagInput
                    tags={settings.permissions?.allow || []}
                    onChange={(tags) => updatePermissions('allow', tags)}
                    placeholder="Bash(npm run *)"
                  />
                </div>

                <div>
                  <label className="block text-xs text-slate-500 mb-1">
                    Deny rules <span className="text-slate-400">e.g. Bash(rm *), Read(.env)</span>
                  </label>
                  <TagInput
                    tags={settings.permissions?.deny || []}
                    onChange={(tags) => updatePermissions('deny', tags)}
                    placeholder="Bash(rm -rf *)"
                  />
                </div>

                <div>
                  <label className="block text-xs text-slate-500 mb-1">
                    Ask rules <span className="text-slate-400">prompt before executing</span>
                  </label>
                  <TagInput
                    tags={settings.permissions?.ask || []}
                    onChange={(tags) => updatePermissions('ask', tags)}
                    placeholder="Bash(git push *)"
                  />
                </div>
              </fieldset>

              {/* Model & Effort */}
              <fieldset className="space-y-3">
                <legend className="text-sm font-medium text-slate-700 dark:text-slate-300">Defaults</legend>
                <div className="flex gap-4">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Model</label>
                    <select
                      value={settings.model || ''}
                      onChange={(e) => updateSettings({ model: e.target.value || undefined })}
                      className="w-56 px-2 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100"
                    >
                      {MODELS.map((m) => (
                        <option key={m} value={m}>
                          {m || '(inherit)'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Effort level</label>
                    <select
                      value={settings.effortLevel || ''}
                      onChange={(e) => updateSettings({ effortLevel: e.target.value || undefined })}
                      className="w-32 px-2 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100"
                    >
                      {EFFORT_LEVELS.map((l) => (
                        <option key={l} value={l}>
                          {l || '(inherit)'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </fieldset>

              {/* Environment Variables */}
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-slate-700 dark:text-slate-300">Environment variables</legend>
                {Object.entries(settings.env || {}).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2 text-xs font-mono">
                    <span className="text-slate-600 dark:text-slate-400">{key}</span>
                    <span className="text-slate-400">=</span>
                    <span className="text-slate-900 dark:text-slate-100">{value}</span>
                    <button
                      onClick={() => removeEnvVar(key)}
                      className="text-red-400 hover:text-red-600 text-xs"
                    >
                      remove
                    </button>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={envKey}
                    onChange={(e) => setEnvKey(e.target.value)}
                    placeholder="KEY"
                    className="w-32 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-mono text-slate-900 dark:text-slate-100"
                  />
                  <span className="text-slate-400">=</span>
                  <input
                    type="text"
                    value={envVal}
                    onChange={(e) => setEnvVal(e.target.value)}
                    placeholder="value"
                    className="w-48 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-mono text-slate-900 dark:text-slate-100"
                  />
                  <button
                    onClick={addEnvVar}
                    className="px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                  >
                    Add
                  </button>
                </div>
              </fieldset>
            </>
          )}

          <SaveToRepoButton saving={config.saving} saved={config.saved} error={config.error} onSave={() => config.save()} />
        </div>
      )}
    </CollapsibleSection>
  );
}
